import React, { useState, useEffect, useCallback } from 'react'
import { useFormatMessage } from '../../utils/intlHelpers'
import { RootState } from '../../store'
import { ApexCash, ApexAccounts } from 'apex-ui-services'
import { AmountAvailableResponsePayload, AccountDetailResponsePayload } from '@Types'
import { AlertStatus } from '@chakra-ui/react'
import { connect } from 'react-redux'
import { MessageCenter } from '../../components/MessageCenter'
import { CreateTransferState, IraState, AuthorizationState, CancelTransferState } from '@Types'
import isEmpty from 'lodash.isempty'
import { hasViewTransfersAccess } from '../../utils/permissionsHelper'
import { useAlert, Alert } from '@apex/component-library'
import { useToast } from '@chakra-ui/react'
import { useGetAchRel } from '../../api/useGetAchRel'
import useAccount from '../../hooks/useAccount'

interface Props {
  amountAvailable?: AmountAvailableResponsePayload | null
  createTransfer?: CreateTransferState
  ira?: IraState
  auth?: AuthorizationState
  accountDetails?: AccountDetailResponsePayload | null
  cancelAchTransfer?: CancelTransferState
  clearAchTransfer: () => void
}

const TransferMessagesComponent = ({
  amountAvailable,
  createTransfer,
  ira,
  auth,
  accountDetails,
  cancelAchTransfer,
  clearAchTransfer
}: Props) => {
  const formatMessage = useFormatMessage()
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [infoMessages, setInfoMessages] = useState<string[]>([])
  const [successMessages, setSuccessMessages] = useState<string[]>([])
  const { setAlert, removeAllAlerts, removeAlert } = useAlert()
  const removeAllAlertsMemo = useCallback(removeAllAlerts, [])
  const toast = useToast()
  const { account } = useAccount()
  const { data: achRel, isError: achRelIsError, error: achRelError } = useGetAchRel(account?.accountNumber!)

  useEffect(() => {
    setErrorMessages([])
    setInfoMessages([])
    setSuccessMessages([])
    removeAllAlertsMemo()
  }, [account, removeAllAlertsMemo])

  const setCreateTransferMessages = (
    createTransfer?: CreateTransferState,
    amountAvailable?: AmountAvailableResponsePayload | null,
    ira?: IraState | null
  ) => {
    const success: string[] = []

    if (
      createTransfer?.achTransfer &&
      amountAvailable?.total &&
      createTransfer?.achTransfer?.amount === amountAvailable?.total &&
      ira?.constraints?.distributionConstraints?.totalDistributionAllowed === true &&
      ira?.constraints?.retirementAccount === true
    ) {
      success.push(...[formatMessage('transfer.success'), formatMessage('transfer.success.fullDisbursement')])
    } else if (
      createTransfer?.achTransfer &&
      amountAvailable?.total &&
      createTransfer?.achTransfer?.amount === amountAvailable?.total
    ) {
      success.push(...[formatMessage('transfer.success'), formatMessage('transfer.success.fullDisbursement')])
    } else if (
      createTransfer?.achTransfer &&
      amountAvailable?.total &&
      createTransfer?.achTransfer?.amount === amountAvailable?.total
    ) {
      success.push(...[formatMessage('transfer.success.allAssets'), formatMessage('transfer.success.allAssets.note')])
    } else if (createTransfer?.achTransfer) {
      success.push(formatMessage('transfer.success'))
    }
    return success
  }

  let errorAlertIds: string[] = []

  const addAlert = (status: AlertStatus, message: string, title: string = ''): void => {
    const { id } = setAlert({
      title: title,
      description: message,
      status,
      direction: 'row',
      isClosable: true,
      showIcon: true
    })
    errorAlertIds.push(id)
  }

  useEffect(() => {
    const infoMsgs: string[] = []
    const successMsgs: string[] = []
    if (hasViewTransfersAccess(auth?.authorization?.permissions)) {
      if (!isEmpty(createTransfer)) {
        const success = setCreateTransferMessages(createTransfer, amountAvailable, ira)
        successMsgs.push(...success)
      }
      if (cancelAchTransfer?.cancelTransfer) {
        successMsgs.push(formatMessage('transfer.cancelTransfer.success'))
      }

      if (accountDetails?.restrictionCode) {
        addAlert(
          'warning',
          formatMessage('transfer.accountRestriction', {
            code: accountDetails?.restrictionCode
          }),
          formatMessage('transfer.restrictedAccount')
        )
      } else {
        determineErrorMessages()
      }
    }

    setSuccessMessages(successMsgs)
    setInfoMessages(infoMsgs)

    return () => {
      errorAlertIds.forEach((id) => removeAlert(id))
    }
    // eslint-disable-next-line
  }, [auth, createTransfer, achRel, achRelError, ira, cancelAchTransfer, accountDetails, achRelIsError])

  const determineErrorMessages = () => {
    // Priority of error messages - only one should be shown to the user at a time
    if (!isEmpty(createTransfer?.error)) {
      if (createTransfer?.error?.status && createTransfer.error.status >= 500) {
        addAlert('error', formatMessage('common.serviceUnavailable'), formatMessage('common.systemUnavailable'))
      } else {
        toast({
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
          title: 'The request failed.',
          description: createTransfer?.error?.body?.errorDescription || formatMessage('transfer.createTransfer.error'),
          status: 'error',
          duration: null,
          render: ({ onClose, id }) => (
            <Alert
              sx={{
                width: 340,
                color: 'black',
                bg: 'white',
                boxShadow: '0px 0px 5px 3px rgba(153,153,153,0.5)'
              }}
              direction='column'
              variant='left-accent'
              title='The request failed.'
              description={
                createTransfer?.error?.body?.errorDescription || formatMessage('transfer.createTransfer.error')
              }
              status='error'
              isClosable
              dismiss={onClose}
              id={id as string}
            />
          )
        })
        // addAlert('error', createTransfer?.error?.body.errorDescription || formatMessage('transfer.createTransfer.error'), 'The request failed.')
      }
    } else if (!isEmpty(cancelAchTransfer?.error)) {
      if (cancelAchTransfer?.error?.status && cancelAchTransfer.error.status >= 500) {
        addAlert('error', formatMessage('common.serviceUnavailable'), formatMessage('common.systemUnavailable'))
      } else {
        toast({
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
          title: 'The request failed.',
          description:
            cancelAchTransfer?.error?.body?.errorDescription || formatMessage('transfer.cancelTransfer.error'),
          status: 'error',
          duration: null,
          render: ({ onClose, id }) => (
            <Alert
              sx={{
                width: 340,
                color: 'black',
                bg: 'white',
                boxShadow: '0px 0px 5px 3px rgba(153,153,153,0.5)'
              }}
              direction='column'
              variant='left-accent'
              title='The request failed.'
              description={
                cancelAchTransfer?.error?.body?.errorDescription || formatMessage('transfer.cancelTransfer.error')
              }
              status='error'
              isClosable
              dismiss={onClose}
              id={id as string}
            />
          )
        })
        // addAlert('error', cancelAchTransfer?.error?.body.errorDescription || formatMessage('transfer.cancelTransfer.error'), 'The request failed.')
      }
    } else if (achRelError?.response?.status! >= 500 || (ira?.error?.status && ira?.error?.status >= 500)) {
      addAlert('error', formatMessage('common.serviceUnavailable'), formatMessage('common.systemUnavailable'))
    } else if (achRelError?.response?.statusText || ira?.error?.body?.errorDescription) {
      ;[achRelError?.response?.statusText, ira?.error?.body].forEach((body) => {
        if (body && body.errorDescription && body.errorCode) {
          addAlert('error', `${body?.errorCode}: ${body?.errorDescription}`)
        }
      })
    }
  }

  return (
    <MessageCenter
      errorMessages={errorMessages}
      infoMessages={infoMessages}
      successMessages={successMessages}
      onClose={clearAchTransfer}
    />
  )
}

const mapStateToProps = (state: RootState) => {
  const legitAuthState = (state as any)?.APEX_LEGIT_AUTHORIZATION
  return {
    accountDetails: ApexAccounts.accounts.selectors.accountDetails(state),
    amountAvailable: ApexCash.amountAvailable.selectors.amountAvailable(state),
    createTransfer: {
      achTransfer: ApexCash.achTransfers.selectors.achTransfer(state),
      loading: ApexCash.achTransfers.selectors.isProcessing(state),
      error: ApexCash.achTransfers.selectors.error(state)
    },
    ira: {
      constraints: ApexCash.iraConstraints.selectors.iraConstraints(state),
      constraintsDirection: ApexCash.iraConstraints.selectors.iraConstraintsDirection(state),
      loading: ApexCash.iraConstraints.selectors.isProcessing(state),
      error: ApexCash.iraConstraints.selectors.error(state)
    },
    auth: {
      authorization: legitAuthState?.results
    },
    cancelAchTransfer: ApexCash.achTransfers.selectors.cancelAchTransferState(state)
  }
}

const mapDispatchToProps = {
  clearAchTransfer: ApexCash.achTransfers.actions.clearAchTransfer
}

export const TransferMessages = connect(mapStateToProps, mapDispatchToProps)(TransferMessagesComponent)
